import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "react-bootstrap";
import Icon from "@mdi/react";
import { mdiSquareEditOutline } from "@mdi/js";
import FsLightbox from "fslightbox-react";
import EditCoverPage from "./editCoverPage";
import AIProcessingBlock from "./aiProcessingBlock";
import EditItemForm from "./editItemForm";
import {
  enterEdit,
  deleteItem,
  fetchStructuredReport,
  dismissAIProcessingItem,
  retryAIProcessingItem,
} from "./slice";
import { clearRetryPayload } from "./retryRegistry";
import { PLURAL_TO_CAPITAL, badgeFor } from "./editItemFormConstants";
import "./webReportEdit.css";

// Single-row photo grid for the read-only item card. Tile size mirrors the
// CSS (180×180 desktop, 120×120 below 600px); overflow collapses into a
// "+N" overlay on the last visible tile.
const PHOTO_GAP = 8;
const SingleRowPhotoGrid = ({ images, alt, onOpenTile }) => {
  const containerRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(images.length);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    // Mobile hard-caps at 2 tiles; desktop measures how many fit in the row.
    const measure = () => {
      const isMobile = window.matchMedia("(max-width: 600px)").matches;
      if (isMobile) {
        setVisibleCount(Math.min(2, images.length));
        return;
      }
      const width = el.clientWidth;
      const fits = Math.max(
        1,
        Math.floor((width + PHOTO_GAP) / (180 + PHOTO_GAP))
      );
      setVisibleCount(Math.min(fits, images.length));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [images.length]);

  const visible = images.slice(0, visibleCount);
  const overflow = images.length - visibleCount;

  return (
    <div
      ref={containerRef}
      className="edit-images-grid edit-images-grid-single-row"
    >
      {visible.map((image, idx) => {
        const isLast = idx === visible.length - 1;
        const showOverflow = isLast && overflow > 0;
        return (
          <div
            key={image.id}
            className={`edit-image-container${
              showOverflow ? " edit-image-overflow" : ""
            }`}
            role="button"
            tabIndex={0}
            onClick={() => onOpenTile(idx)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onOpenTile(idx);
              }
            }}
          >
            <img
              src={image.annotated_image?.presigned_url || image.presigned_url}
              alt={alt}
            />
            {showOverflow && (
              <span className="edit-image-overflow-badge">+{overflow}</span>
            )}
          </div>
        );
      })}
    </div>
  );
};

// Stable identity for a code — falls back to (code|section) when `code_id`
// is missing or duplicated across AI suggestions.
const codeKey = (c) =>
  c?.code_id ? `id:${c.code_id}` : `cs:${c?.code ?? ""}|${c?.section ?? ""}`;

const draftFromItem = (item) => ({
  name: item.name || "",
  description: item.description || "",
  cause: item.cause || "",
  solution: item.solution || "",
  hint: item.hint || "",
  selectedCodeIds: (item.codes_selected || []).map(codeKey),
});

export { codeKey, draftFromItem };

const EditContent = ({
  reportContent,
  coverPhotoRef,
  onSelectCoverPhotoFile,
  coverUploadLoading,
  onToast,
}) => {
  const { sections, profile_image } = reportContent;
  const dispatch = useDispatch();
  const aiProcessingItems = useSelector(
    (state) => state.webReportEdit.aiProcessing.items
  );
  // Map of itemId → type for every item currently open in edit mode. Reading
  // this map (rather than a single id) lets multiple items render their form
  // at once; it only changes on enter/exit, so keystrokes don't re-render here.
  const editingTypeById = useSelector(
    (state) => state.webReportEdit.edit.editingTypeById
  );

  const handleEnterEdit = (item) => {
    dispatch(
      enterEdit({
        itemId: item.id,
        type: PLURAL_TO_CAPITAL[item.type],
        draft: draftFromItem(item),
      })
    );
  };
  // `displayMode === "inline"` (Modify Observation) renders at the edited
  // item's location; everything else renders at the top (Add New Item).
  // Failed items stay rendered (as the S6 failed card) — only SUCCESS drops
  // out (it resolves into the finished item). See AS-902.
  const reportId = reportContent?.id;
  const handleRetryProcessing = async (procItem) => {
    const res = await dispatch(
      retryAIProcessingItem({ taskId: procItem.taskId })
    );
    if (!res?.ok) {
      onToast?.({
        message: "Couldn't start retry. Please try again.",
        variant: "error",
      });
    }
  };
  // Create-fail: delete the server item and clear its card + widget row.
  const handleDiscardProcessing = (procItem) => {
    if (procItem.itemId) {
      dispatch(deleteItem({ type: procItem.type, itemId: procItem.itemId }));
    }
    clearRetryPayload(procItem.taskId);
    dispatch(dismissAIProcessingItem(procItem.taskId));
  };
  // Edit-fail: keep the server item (reverts to its last-saved result); clear
  // the card + row and refetch to resync.
  const handleCancelProcessing = (procItem) => {
    clearRetryPayload(procItem.taskId);
    dispatch(dismissAIProcessingItem(procItem.taskId));
    if (reportId) dispatch(fetchStructuredReport(reportId));
  };
  const processingActions = {
    onRetry: handleRetryProcessing,
    onDiscard: handleDiscardProcessing,
    onCancel: handleCancelProcessing,
    onToast,
  };

  const active = aiProcessingItems.filter((i) => i.taskState !== "SUCCESS");
  const inProgressItems = active.filter((i) => i.displayMode !== "inline");
  const inlineProcessingByItemId = new Map(
    active
      .filter((i) => i.displayMode === "inline" && i.itemId)
      .map((i) => [i.itemId, i])
  );

  // Key-counter pattern: reopening on a different image re-renders the gallery.
  const [lightboxSources, setLightboxSources] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxToggler, setLightboxToggler] = useState(false);
  const [lightboxKey, setLightboxKey] = useState(0);
  const [lightboxRequested, setLightboxRequested] = useState(false);

  useEffect(() => {
    if (lightboxRequested) {
      setLightboxToggler((prev) => !prev);
      setLightboxRequested(false);
    }
  }, [lightboxKey, lightboxRequested]);

  const openLightbox = (images, index) => {
    const sources = images
      .map((img) => img.annotated_image?.presigned_url || img.presigned_url)
      .filter((url) => !!url);
    if (!sources.length) return;
    setLightboxSources(sources);
    setLightboxIndex(index);
    setLightboxKey((k) => k + 1);
    setLightboxRequested(true);
  };

  return (
    <div className="edit-content">
      <EditCoverPage
        ref={coverPhotoRef}
        profileImage={profile_image?.presigned_url}
        onSelectCoverPhotoFile={onSelectCoverPhotoFile}
        uploading={coverUploadLoading}
        reportId={reportContent?.id}
        initialSummary={reportContent?.summary}
        onToast={onToast}
      />

      {inProgressItems.map((item) => (
        <AIProcessingBlock
          key={item.taskId}
          item={item}
          actions={processingActions}
        />
      ))}

      {(sections || []).map((section, sectionIdx) => {
        const sectionId = `section-${section.name?.toLowerCase().replace(/\s+/g, "-")}`;

        return (
          <div
            key={section.id || `${section.name}-${sectionIdx}`}
            id={sectionId}
            className="edit-section"
          >
            <h2 className="edit-section-title">{section.name}</h2>

            {(section.overviews || []).map((overview) => {
              const overviewItem = {
                ...overview,
                type: "overviews",
                section: section.name,
              };
              if (editingTypeById[overview.id]) {
                return (
                  <EditItemForm
                    key={overview.id}
                    item={overviewItem}
                    onToast={onToast}
                  />
                );
              }
              const inlineProc = inlineProcessingByItemId.get(overview.id);
              if (inlineProc) {
                return (
                  <AIProcessingBlock
                    key={inlineProc.taskId}
                    item={inlineProc}
                    actions={processingActions}
                  />
                );
              }
              return (
                <div key={overview.id} id={overview.id} className="edit-item">
                  <div className="edit-item-header">
                    <div className="edit-item-header-left">
                      <span className="edit-item-name">Overview</span>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="edit-item-modify-btn"
                        onClick={() => handleEnterEdit(overviewItem)}
                      >
                        <Icon path={mdiSquareEditOutline} size={0.667} />
                        Modify
                      </Button>
                    </div>
                  </div>

                  <div className="edit-item-content">
                    {overview.description && (
                      <div className="edit-item-field">
                        <span className="edit-item-value">
                          {overview.description}
                        </span>
                      </div>
                    )}
                    {overview.images && overview.images.length > 0 && (
                      <SingleRowPhotoGrid
                        images={overview.images}
                        alt={`${section.name} overview`}
                        onOpenTile={(idx) => openLightbox(overview.images, idx)}
                      />
                    )}
                  </div>
                </div>
              );
            })}

            {(section.subsections || []).map((subsection, subIdx) => {
              const subsectionId = `subsection-${subsection.name?.toLowerCase().replace(/\s+/g, "-")}`;
              // Inject the parent section/subsection names — the structured
              // response nests items without them, but the Move modal needs
              // them to preselect the item's current location (AS-903).
              const location = {
                section: section.name,
                subsection: subsection.name,
              };
              const items = [
                ...(subsection.issues || []).map((i) => ({
                  ...i,
                  ...location,
                  type: "issues",
                })),
                ...(subsection.infos || []).map((i) => ({
                  ...i,
                  ...location,
                  type: "infos",
                })),
                ...(subsection.limits || []).map((i) => ({
                  ...i,
                  ...location,
                  type: "limits",
                })),
              ];
              const totalItems = items.length;

              return (
                <div
                  key={subsection.id || `${subsection.name}-${subIdx}`}
                  id={subsectionId}
                  className="edit-subsection"
                >
                  <div className="edit-subsection-header">
                    <h3 className="edit-subsection-title">{subsection.name}</h3>
                    {totalItems > 0 && (
                      <span className="edit-subsection-count">
                        {totalItems} {totalItems === 1 ? "item" : "items"}
                      </span>
                    )}
                  </div>

                  {items.map((item) => {
                    if (editingTypeById[item.id]) {
                      return (
                        <EditItemForm
                          key={`${item.type}-${item.id}`}
                          item={item}
                          onToast={onToast}
                        />
                      );
                    }
                    const inlineProc = inlineProcessingByItemId.get(item.id);
                    if (inlineProc) {
                      return (
                        <AIProcessingBlock
                          key={inlineProc.taskId}
                          item={inlineProc}
                          actions={processingActions}
                        />
                      );
                    }
                    const { label: typeLabel, className: typeClass } =
                      badgeFor(item);

                    return (
                      <div
                        key={`${item.type}-${item.id}`}
                        id={item.id}
                        className="edit-item"
                      >
                        <div className="edit-item-header">
                          <div className="edit-item-header-left">
                            <span className="edit-item-name">
                              {item.name || "Untitled"}
                            </span>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="edit-item-modify-btn"
                              onClick={() => handleEnterEdit(item)}
                            >
                              <Icon path={mdiSquareEditOutline} size={0.667} />
                              Modify
                            </Button>
                          </div>
                          <span className={`edit-item-badge ${typeClass}`}>
                            {typeLabel}
                          </span>
                        </div>

                        <div className="edit-item-content">
                          {item.description && (
                            <div className="edit-item-field">
                              <span className="edit-item-label">
                                Description
                              </span>
                              <span className="edit-item-value">
                                {item.description}
                              </span>
                            </div>
                          )}

                          {item.cause && (
                            <div className="edit-item-field">
                              <span className="edit-item-label">Cause</span>
                              <span className="edit-item-value">
                                {item.cause}
                              </span>
                            </div>
                          )}

                          {item.solution && (
                            <div className="edit-item-field">
                              <span className="edit-item-label">Solution</span>
                              <span className="edit-item-value">
                                {item.solution}
                              </span>
                            </div>
                          )}

                          {item.codes_selected?.length > 0 && (
                            <div className="edit-item-field">
                              <span className="edit-item-label">
                                Potential Violation(s)
                              </span>
                              <span className="edit-item-value">
                                {item.codes_selected.map((c, i) => (
                                  <span
                                    key={`${codeKey(c)}-${i}`}
                                    className="edit-item-code-entry"
                                  >
                                    <span className="edit-item-code-heading">
                                      {c.code} {c.section}
                                    </span>
                                    <span className="edit-item-code-content">
                                      {c.content}
                                    </span>
                                  </span>
                                ))}
                              </span>
                            </div>
                          )}

                          {item.images && item.images.length > 0 && (
                            <SingleRowPhotoGrid
                              images={item.images}
                              alt={item.name || "Item"}
                              onOpenTile={(idx) =>
                                openLightbox(item.images, idx)
                              }
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        );
      })}

      <FsLightbox
        toggler={lightboxToggler}
        sources={lightboxSources}
        sourceIndex={lightboxIndex}
        type="image"
        key={lightboxKey}
      />
    </div>
  );
};

export default EditContent;
